/**
 * O seletor de período do Portal — as MESMAS dez opções da SPA interna (achado TIME-01).
 *
 * A regra ("Este mês" é o mês-calendário completo; "Este trimestre" começa em jan/abr/
 * jul/out) mora em `@dv/domain` desde o lote P2, e é a mesma função que a tela interna
 * chama. Antes disto, o Portal calculava o próprio mês com `new Date()` e três linhas
 * de aritmética local: duas superfícies com contas separadas para a mesma palavra, e o
 * cliente externo lendo um "Este mês" que não era o mesmo da equipe.
 *
 * O DESENHO é diferente de propósito — a interna tem um popover com atalhos, aqui é um
 * `<select>` nativo. O que não pode diferir é o RECORTE.
 */
import { useState } from 'react'
import {
  ehDataNominal,
  OPCOES_DE_PERIODO,
  resolverPeriodo,
  rotularPeriodo,
  type DataNominal,
  type Periodo,
} from '@dv/domain'
import { useAviso } from './toast.js'
import { CampoDeDataComCapa, SeletorComCapa } from './capa.js'

export { rotularPeriodo }

export function SeletorDePeriodo({
  hoje,
  periodo,
  aoTrocar,
}: {
  hoje: DataNominal
  periodo: Periodo
  aoTrocar: (periodo: Periodo) => void
}) {
  const [de, setDe] = useState<string>(periodo.de)
  const [ate, setAte] = useState<string>(periodo.ate)
  const avisar = useAviso()

  const personalizado = periodo.chave === 'personalizado'

  function trocarChave(chave: string): void {
    const escolhida = OPCOES_DE_PERIODO.find((o) => o.chave === chave)?.chave
    if (escolhida === undefined) return
    const resolvido = resolverPeriodo(escolhida, hoje)
    setDe(resolvido.de)
    setAte(resolvido.ate)
    aoTrocar(resolvido)
  }

  function aplicarPersonalizado(inicio: string, fim: string): void {
    setDe(inicio)
    setAte(fim)
    if (!ehDataNominal(inicio) || !ehDataNominal(fim)) return

    /*
      Data inicial depois da final: o Portal CORRIGE e avisa, em vez de recusar.

      Quem digita 20/07 no primeiro campo e 01/07 no segundo quer o intervalo entre os
      dois — inverter é o que a pessoa pediu, dito na ordem errada. Recusar em silêncio
      deixaria a tela sem dado nenhum e sem explicação.
    */
    const invertido = inicio > fim
    if (invertido) {
      /*
        O aviso vai para a BANDEJA, e não mais para um parágrafo ao lado dos campos.

        Uma região viva que nasce junto com o texto não é anunciada: o leitor de tela
        precisa já estar observando a região quando a frase aparece nela. A bandeja
        existe desde a montagem da página; o parágrafo nascia com a frase dentro.
      */
      avisar({
        tom: 'info',
        titulo: 'Intervalo ajustado',
        texto: 'A data inicial era maior que a final, invertemos o intervalo.',
        teste: 'intervalo-ajustado',
      })
    }
    aoTrocar({ chave: 'personalizado', de: invertido ? fim : inicio, ate: invertido ? inicio : fim })
  }

  return (
    <div className="filtro" data-teste="filtro-periodo">
      <label htmlFor="portal-periodo">Período</label>
      <SeletorComCapa
        id="portal-periodo"
        larguraPeloValor
        value={periodo.chave}
        onChange={(evento) => {
          trocarChave(evento.target.value)
        }}
      >
        {OPCOES_DE_PERIODO.map((opcao) => (
          <option key={opcao.chave} value={opcao.chave}>
            {opcao.rotulo}
          </option>
        ))}
      </SeletorComCapa>

      {personalizado && (
        <span className="periodo-personalizado">
          <CampoDeDataComCapa
            aria-label="De"
            value={de}
            onChange={(evento) => {
              aplicarPersonalizado(evento.target.value, ate)
            }}
          />
          <CampoDeDataComCapa
            aria-label="Até"
            value={ate}
            onChange={(evento) => {
              aplicarPersonalizado(de, evento.target.value)
            }}
          />
        </span>
      )}
    </div>
  )
}

/** "· Este mês — 1–31 jul 2026" (D-77), ao lado do eyebrow de boas-vindas. */
export function rotuloCompleto(periodo: Periodo): string {
  const nome = OPCOES_DE_PERIODO.find((o) => o.chave === periodo.chave)?.rotulo ?? ''
  return `${nome} · ${rotularPeriodo(periodo)}`
}
